import { CSSResultGroup, LitElement, html, type TemplateResult } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { style } from './variant-matrix-filter-slide-in.style.js'
import { type CSECheckbox, type CSESlideIn } from '@cloudsuite/mosaic-elements'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { FilterRemovedEvent, FiltersClearedEvent } from 'components/discovery/plp/filters/plp-filters/events.js'
import { VariantMatrixFiltersApplyEvent } from 'components/discovery/pdp/variant-matrix/variant-matrix-events.js'
import 'components/discovery/plp/filters/active-filters/active-filters.js'
import type {
  VariantMatrixFilter,
  VariantMatrixFilterOption,
  VariantMatrixSelectedFilter,
} from 'components/discovery/pdp/variant-matrix/typing/variant-matrix-typing.js'
import {
  getStockFilterOptionLabel,
  getStockFilterSectionLabel,
  isStockSelectedFilter,
} from 'components/discovery/pdp/variant-matrix/variant-matrix-stock-filter.js'

@customElement('csc-variant-matrix-filter-slide-in')
export class CSCVariantMatrixFilterSlideIn extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Array })
  filters: VariantMatrixFilter[] = []

  @property({ type: Array, attribute: false })
  selectedFilters: VariantMatrixSelectedFilter[] = []

  @state()
  private draftSelections: Record<string, boolean> = {}

  @state()
  private draftStockOnly = false

  @state()
  private loading = false

  private initialSelections: Record<string, boolean> = {}
  private initialStockOnly = false

  @query('cse-slide-in')
  private slideIn!: CSESlideIn

  public open(): void {
    this.draftSelections = this.getSelectionMapFromFilters(this.filters)
    this.initialSelections = { ...this.draftSelections }
    const stockSelected = this.selectedFilters.some(isStockSelectedFilter)
    this.draftStockOnly = stockSelected
    this.initialStockOnly = stockSelected
    this.loading = false
    this.slideIn.open()
  }

  public close(): void {
    this.slideIn.close()
  }

  public syncAppliedState(filters: VariantMatrixFilter[], stockOnly: boolean): void {
    this.draftSelections = this.getSelectionMapFromFilters(filters)
    this.initialSelections = { ...this.draftSelections }
    this.draftStockOnly = stockOnly
    this.initialStockOnly = stockOnly
    this.loading = false
  }

  private selectionKey(filterId: number, optionId: number): string {
    return `${filterId}:${optionId}`
  }

  private getFilterOptions(filter: VariantMatrixFilter): VariantMatrixFilterOption[] {
    return filter.options ?? filter.values ?? []
  }

  private getSelectionMapFromFilters(filters: VariantMatrixFilter[]): Record<string, boolean> {
    const map: Record<string, boolean> = {}
    for (const filter of filters) {
      for (const option of this.getFilterOptions(filter)) {
        map[this.selectionKey(filter.id, option.id)] = option.selected
      }
    }
    return map
  }

  private isOptionChecked(filter: VariantMatrixFilter, option: VariantMatrixFilterOption): boolean {
    return this.draftSelections[this.selectionKey(filter.id, option.id)] ?? false
  }

  private onDraftOptionChange(
    filter: VariantMatrixFilter,
    option: VariantMatrixFilterOption,
    event: Event,
  ) {
    const checkbox = event.currentTarget as CSECheckbox
    this.draftSelections = {
      ...this.draftSelections,
      [this.selectionKey(filter.id, option.id)]: checkbox.checked,
    }
  }

  private parseSelectionKey(key: string): { filterId: number; optionId: number } | null {
    const separatorIndex = key.indexOf(':')
    if (separatorIndex === -1) return null

    const filterId = Number(key.slice(0, separatorIndex))
    const optionId = Number(key.slice(separatorIndex + 1))
    if (Number.isNaN(filterId) || Number.isNaN(optionId)) return null

    return { filterId, optionId }
  }

  private getApplyChanges() {
    const changes: { filterId: number; optionId: number; selected: boolean }[] = []
    for (const [key, selected] of Object.entries(this.draftSelections)) {
      const initial = this.initialSelections[key] ?? false
      if (selected === initial) continue

      const parsed = this.parseSelectionKey(key)
      if (!parsed) continue

      changes.push({ ...parsed, selected })
    }
    return changes
  }

  private onDraftFilterRemoved(event: FilterRemovedEvent) {
    event.stopPropagation()

    if (isStockSelectedFilter(event.filter)) {
      this.draftStockOnly = false
      return
    }

    for (const filter of this.filters) {
      for (const option of this.getFilterOptions(filter)) {
        const label = `${filter.name} - ${option.name}`
        const removed = event.filter
        const removedName = removed.name ?? ''
        const matches = removedName === label
          || removedName === option.name
          || (removedName.startsWith(`${filter.name}`) && removedName.includes(option.name))

        if (!matches) continue

        this.draftSelections = {
          ...this.draftSelections,
          [this.selectionKey(filter.id, option.id)]: false,
        }
        return
      }
    }
  }

  private onDraftFiltersCleared(event: FiltersClearedEvent) {
    event.stopPropagation()
    this.draftStockOnly = false
    this.draftSelections = Object.fromEntries(
      Object.keys(this.draftSelections).map(key => [key, false]),
    )
  }

  private onDraftStockChange(event: Event) {
    this.draftStockOnly = (event.currentTarget as CSECheckbox).checked
  }

  private renderStockFilter(): TemplateResult {
    return html`
      <cse-accordion variant="panel" ?open=${true}>
        <div slot="title">
          <cse-text variant="heading-4">${getStockFilterSectionLabel()}</cse-text>
        </div>
        <div slot="content">
          <cse-list>
            <cse-list-item>
              <cse-checkbox
                ?checked=${this.draftStockOnly}
                @change=${this.onDraftStockChange}
              >
                ${getStockFilterOptionLabel()}
              </cse-checkbox>
            </cse-list-item>
          </cse-list>
        </div>
      </cse-accordion>
    `
  }

  private onApply() {
    const changes = this.getApplyChanges()
    const stockChanged = this.draftStockOnly !== this.initialStockOnly

    if (changes.length || stockChanged) {
      this.dispatchEvent(new VariantMatrixFiltersApplyEvent(changes, this.draftStockOnly))
    } else {
      this.close()
    }
  }

  override render(): TemplateResult {
    return html`
      <cse-slide-in size="default">
        <div slot="header">
          ${t('filters_header', { ns: 'csc-variant-matrix-filter-slide-in', default: 'Filters' })}
        </div>

        <div class="filters-content">
          ${this.loading ? html`<div class="loading-overlay"></div>` : null}
          <csc-active-filters
            class="active-filters"
            hidden-fields="filter-name"
            .filters=${this.selectedFilters}
            @filter-removed=${this.onDraftFilterRemoved}
            @filters-cleared=${this.onDraftFiltersCleared}
          ></csc-active-filters>
          ${this.selectedFilters.length ? html`<cse-divider orientation="horizontal" variant="solid"></cse-divider>` : null}

          ${this.renderStockFilter()}

          ${repeat(this.filters, filter => filter.id, filter => html`
            <cse-accordion variant="panel" ?open=${true}>
              <div slot="title">
                <cse-text variant="heading-4">${filter.name}</cse-text>
              </div>
              <div slot="content">
                <cse-list>
                  ${repeat(
                    this.getFilterOptions(filter),
                    option => option.id,
                    option => html`
                      <cse-list-item>
                        <cse-checkbox
                          ?checked=${this.isOptionChecked(filter, option)}
                          ?disabled=${option.unavailable}
                          @change=${(event: Event) => this.onDraftOptionChange(filter, option, event)}
                        >
                          ${option.name}
                        </cse-checkbox>
                      </cse-list-item>
                    `,
                  )}
                </cse-list>
              </div>
            </cse-accordion>
          `)}
        </div>

        <cse-button slot="footer" fit="fill" variant="primary" @click=${this.onApply}>
          ${t('apply', { ns: 'csc-variant-matrix-filter-slide-in', default: 'Apply' })}
        </cse-button>
      </cse-slide-in>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-variant-matrix-filter-slide-in': CSCVariantMatrixFilterSlideIn
  }
}
